// Checklist.jsx
import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import "./Checklist.css";
import DatePicker from "../../components/DatePicker/DatePicker";
import Dropdown from "../../components/Dropdown/Dropdown";
import ModuleSidebar from "../../components/ModuleSidebar/ModuleSidebar";
import Services from "../../services/Services";
import { GeneralContext } from "../../GeneralProvider";
import AlertBox from "../../components/AlertBox/AlertBox";
import { useLanguage } from "../../LanguageProvider";

export default function Checklist() {
  const { getTranslation } = useLanguage();
  const { users, fetchMembers, selectedProjectID } = useContext(GeneralContext);
  const token =
    typeof window !== "undefined" ? localStorage.getItem("token") : "";

  // === CHECKLISTS & SELECTION ===
  const [lists, setLists] = useState([]); // [{id,title,description,step,elements:[...mapped]}]
  const [activeListId, setActiveListId] = useState("");
  const activeList = useMemo(
    () => lists.find((l) => l.id === activeListId) || null,
    [lists, activeListId]
  );

  // Refs to avoid stale closures inside setTimeout
  const listsRef = useRef(lists);
  const itemsRef = useRef([]);
  const activeListIdRef = useRef(activeListId);

  useEffect(() => {
    listsRef.current = lists;
  }, [lists]);

  useEffect(() => {
    activeListIdRef.current = activeListId;
  }, [activeListId]);

  // === UI STATE FOR CURRENT ITEMS (flat list) ===
  const [items, setItems] = useState([]); // flat list, parentId ile hiyerarşik
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("orta"); // düşük|orta|yüksek (UI)
  const [assigneeId, setAssigneeId] = useState(""); // userID
  const [due, setDue] = useState(null); // Date|null
  const [dueResetKey, setDueResetKey] = useState(0); // DatePicker'ı resetlemek için
  const [filter, setFilter] = useState("all"); // all|active|done
  const [editSignalMap, setEditSignalMap] = useState({});
  const inputRef = useRef(null);
  const [draggingId, setDraggingId] = useState(null); // DnD için

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);

  // === CREATE / EDIT / DELETE MODALS ===
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [newStep, setNewStep] = useState(false); // sadece CREATE sırasında
  const modalTitleInputRef = useRef(null);

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [editTitle, setEditTitle] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editStep, setEditStep] = useState(false); // UI amaçlı
  const editTitleRef = useRef(null);

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchMembers?.();
  }, [selectedProjectID, fetchMembers]);

  // === GLOBAL SYNC LOCK (her UpdateChecklist isteğinde kilit) ===
  const [isSyncing, setIsSyncing] = useState(false);
  const syncInFlightRef = useRef(false);
  const syncTimerRef = useRef(null);

  // ===== Helpers =====
  const mapStepByStep = (val) => val === "1" || val === 1 || val === true;

  function parseServerEndDate(v) {
    if (!v) return null;
    if (typeof v === "string" && v.includes("-"))
      return new Date(v + "T00:00:00");
    const n = Number(v || 0);
    if (n > 0) return new Date(n * 1000);
    return null;
  }

  function mapPriorityToInt(p) {
    return p === "düşük" ? 1 : p === "orta" ? 2 : 3;
  }
  function mapPriorityFromInt(n) {
    if (Number(n) === 1) return "düşük";
    if (Number(n) === 3) return "yüksek";
    return "orta";
  }

  const mapElementToItem = (el) => {
    const dueDate = parseServerEndDate(el?.elementEndDateTime);
    const done = String(el?.elementStatus ?? "") === "2"; // 2: done

    return {
      id: String(el?.elementId || crypto.randomUUID()),
      text: el?.elementDescription || el?.elementTitle || getTranslation("item"),
      done,
      priority: mapPriorityFromInt(el?.elementPriority),
      assignee: el?.elementResponsibleBy || "",
      due: dueDate,
      createdAt:
        Number(el?.elementStartDateTime || Date.now()) *
        (String(el?.elementStartDateTime).length < 11 ? 1000 : 1),
      order:
        typeof el?.elementOrder === "number" ? el.elementOrder : undefined,
      parentId: el?.elementParentId
        ? String(el.elementParentId)
        : null, // HİYERARŞİK YAPI
    };
  };

  const mapChecklistFromAPI = (r) => ({
    id: String(r.checklistID || r.id),
    title: r.title || getTranslation("checklist"),
    description: r.description || "",
    step: mapStepByStep(r.isQueueRestricted),
    createdAt: Number(r.createdDateTime || 0),
    elements: Array.isArray(r.elements) ? r.elements.map(mapElementToItem) : [],
  });

  function normalizeChecklistResponse(res) {
    try {
      const raw = res?.data;
      if (raw && typeof raw === "object") {
        if (Array.isArray(raw)) return raw;
        if (Array.isArray(raw.result)) return raw.result;
        return [];
      }
      if (typeof raw !== "string") return [];
      const cleaned = raw.replace(/<[^>]*>/g, "").trim();
      const jsonStart = cleaned.indexOf("{");
      if (jsonStart === -1) return [];
      const parsed = JSON.parse(cleaned.slice(jsonStart));
      return Array.isArray(parsed?.result) ? parsed.result : [];
    } catch (err) {
      console.error("[Checklist] normalizeChecklistResponse error:", err);
      return [];
    }
  }

  // ===== Fetch lists (STATE + RETURN) =====
  async function loadLists() {
    if (!token || !selectedProjectID) return [];
    try {

      const resp = await Services.GetChecklistsByWorkspaceID(
        token,
        selectedProjectID
      );
      const arr = normalizeChecklistResponse(resp);

      const mapped = arr.map(mapChecklistFromAPI);
      setLists(mapped);
      return mapped;
    } catch (e) {
      console.error("[Checklist] GetChecklistsByWorkspaceID -> error:", e);
      return [];
    }
  }

  // İlk yükleme + proje değişiminde çek
  useEffect(() => {
    (async () => {
      const mapped = await loadLists();
      setActiveListId((prev) =>
        mapped.some((m) => m.id === prev) ? prev : ""
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, selectedProjectID]);

  // === Parent done durumu: tüm sub itemlar done ise parent da done ===
  function recomputeParentDoneState(list) {
    if (!Array.isArray(list) || list.length === 0) return list;

    const byId = new Map();
    list.forEach((it) => {
      byId.set(it.id, { ...it });
    });

    let changed = true;
    while (changed) {
      changed = false;
      const allItems = Array.from(byId.values());
      allItems.forEach((item) => {
        const children = allItems.filter((c) => c.parentId === item.id);
        if (children.length === 0) return;
        const allChildrenDone = children.every((c) => c.done);
        if (item.done !== allChildrenDone) {
          byId.set(item.id, { ...item, done: allChildrenDone });
          changed = true;
        }
      });
    }

    return Array.from(byId.values());
  }

  // LİSTELER/SEÇİM değiştiğinde ekran elemanlarını eşitle (flat items)
  useEffect(() => {
    const found = lists.find((m) => m.id === activeListId);
    const next = found ? [...found.elements] : [];
    next.sort((a, b) => {
      const ao = typeof a.order === "number" ? a.order : 999999;
      const bo = typeof b.order === "number" ? b.order : 999999;
      return ao - bo;
    });
    const withParents = recomputeParentDoneState(next);
    setItems(withParents);
  }, [lists, activeListId]);

  useEffect(() => {
    if (isCreateOpen)
      setTimeout(() => modalTitleInputRef.current?.focus(), 0);
  }, [isCreateOpen]);

  useEffect(() => {
    if (isEditOpen) setTimeout(() => editTitleRef.current?.focus(), 0);
  }, [isEditOpen]);

  // ===== Derived =====
  const activeStep = !!activeList?.step;

  const stats = useMemo(() => {
    const total = items.length;
    const done = items.filter((i) => i.done).length;
    const active = total - done;
    const pct = total ? Math.round((done / total) * 100) : 0;
    return { total, done, active, pct };
  }, [items]);

  const isToggleDisabled = (idx, item) => {
    if (!activeStep) return false;
    if (!item) return false;

    // Zaten tamamlanmış maddeyi geri açmak serbest
    if (item.done) return false;

    const list = itemsRef.current || items;

    // Bir node kendi kardeş grubunda ilk açık (undone) eleman mı?
    const isFirstOpenInGroup = (node) => {
      const siblings = list
        .filter((i) => (i.parentId || null) === (node.parentId || null))
        .slice()
        .sort((a, b) => {
          const ao = typeof a.order === "number" ? a.order : 999999;
          const bo = typeof b.order === "number" ? b.order : 999999;
          return ao - bo;
        });

      const firstOpen = siblings.find((s) => !s.done);

      // Hepsi tamamlanmışsa kısıtlama yok
      if (!firstOpen) return true;

      return String(firstOpen.id) === String(node.id);
    };

    // Kendinden root'a kadar tüm chain'de,
    // her node kendi kardeş grubunda "ilk açık" olmalı.
    // Herhangi bir seviyede bu bozulursa item kilitli.
    let current = item;

    while (current) {
      if (!isFirstOpenInGroup(current)) {
        return true; // bu seviyede önce bitmemiş kardeş var -> kilitli
      }

      if (!current.parentId) break;

      current = list.find(
        (i) => String(i.id) === String(current.parentId)
      );
    }

    return false;
  };

  const getIndexById = (id) =>
    itemsRef.current.findIndex((i) => String(i.id) === String(id));

  // ===== Tree builder (flat -> hierarchical) =====
  const treeItems = useMemo(() => {
    const map = new Map();
    const nodes = items.map((it) => ({ ...it, children: [] }));
    nodes.forEach((n) => map.set(n.id, n));

    const roots = [];
    nodes.forEach((node) => {
      if (node.parentId && map.has(node.parentId)) {
        map.get(node.parentId).children.push(node);
      } else {
        roots.push(node);
      }
    });

    const sortRec = (arr) => {
      arr.sort((a, b) => {
        const ao = typeof a.order === "number" ? a.order : 999999;
        const bo = typeof b.order === "number" ? b.order : 999999;
        return ao - bo;
      });
      arr.forEach((c) => sortRec(c.children));
    };

    sortRec(roots);
    return roots;
  }, [items]);

  const isItemVisible = (item) => {
    if (filter === "all") return true;
    if (filter === "active") return !item.done;
    if (filter === "done") return item.done;
    return true;
  };

  // ===== Options =====
  const priorityOptions = [
    { value: "düşük", label: getTranslation("low") },
    { value: "orta", label: getTranslation("medium") },
    { value: "yüksek", label: getTranslation("high") },
  ];

  const assigneeOptions = useMemo(() => {
    const base = [{ value: "", label: `(${getTranslation("none")})` }];
    if (!Array.isArray(users)) return base;
    const seen = new Set();
    const opts = [];
    for (const u of users) {
      if (u?.status === false) continue;
      const value = String(u.userID);
      const label =
        u.userNameLastname ||
        [u.name, u.lastname].filter(Boolean).join(" ").trim() ||
        u.email ||
        value;
      if (!seen.has(value)) {
        seen.add(value);
        opts.push({ value, label });
      }
    }
    return [...base, ...opts];
  }, [users, getTranslation]);

  const findUserLabelById = (id) => {
    if (!id) return "";
    const u = (users || []).find((x) => String(x.userID) === String(id));
    return (
      u?.userNameLastname ||
      [u?.name, u?.lastname].filter(Boolean).join(" ").trim() ||
      u?.email ||
      id
    );
  };

  // ===== Sidebar menus =====
  const menus = useMemo(
    () => [
      {
        sections: [
          {
            id: "checklists",
            title: getTranslation("checklists"),
            submenu: lists.map((l) => ({
              id: l.id,
              title: l.title,
            })),
          },
        ],
      },
    ],
    [lists, getTranslation]
  );

  // ===== Sidebar callbacks =====
  const handleSidebarClick = (submenuItem) => {
    if (isSyncing) return;
    setActiveListId(submenuItem.id);
    setFilter("all");
  };

  const handleAddEntity = () => {
    if (isSyncing) return;
    setIsCreateOpen(true);
    setNewTitle("");
    setNewDesc("");
    setNewStep(false);
  };

  // === Tarihi lokal YYYY-MM-DD stringe çevir
  const formatDateYYYYMMDDLocal = (d) => {
    if (!(d instanceof Date) || isNaN(d)) return "";
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };

  // ====== PERSISTENCE: UPDATE CHECKLIST WITH ELEMENTS (debounced via refs) ======

  function buildElementsPayload(listItems) {
    return listItems.map((it, idx) => ({
      elementId: it.id?.startsWith?.("tmp-") ? undefined : it.id,
      elementDescription: it.text || "",
      elementResponsibleBy: it.assignee || "",
      elementEndDateTime:
        it.due instanceof Date && !isNaN(it.due)
          ? formatDateYYYYMMDDLocal(it.due)
          : "",
      elementStatus: it.done ? "2" : "1",
      elementPriority: mapPriorityToInt(it.priority),
      elementOrder: typeof it.order === "number" ? it.order : idx,
      elementParentId: it.parentId || null,
    }));
  }

  async function flushSync(reason = "") {
    const tokenLocal = token; // local ref
    const _activeListId = activeListIdRef.current;
    const _lists = listsRef.current;
    const _items = itemsRef.current;

    if (!tokenLocal || !_activeListId) {
      console.warn("[Checklist] flushSync skipped (no token or activeListId)", {
        token: !!tokenLocal,
        activeListId: _activeListId,
        reason,
      });
      return;
    }

    // Zaten bir UpdateChecklist isteği uçuyorsa yenisini başlatma
    if (syncInFlightRef.current) {
      console.warn(
        "[Checklist] flushSync skipped because another UpdateChecklist is in flight",
        { reason }
      );
      return;
    }

    const list = _lists.find((l) => l.id === _activeListId);
    if (!list) {
      console.warn("[Checklist] flushSync skipped (list not found)", {
        _activeListId,
        reason,
      });
      return;
    }

    const elements = buildElementsPayload(_items);

    syncInFlightRef.current = true;
    setIsSyncing(true);

    try {
      const resp = await Services.UpdateChecklist(
        tokenLocal,
        list.title || getTranslation("checklist"),
        elements,
        list.description || "",
        _activeListId
      );
      const reloaded = await loadLists();
    } catch (err) {
      console.error(
        "[Checklist] UpdateChecklist -> error:",
        err,
        "reason:",
        reason
      );
    } finally {
      syncInFlightRef.current = false;
      setIsSyncing(false);
    }
  }

  function scheduleSync(reason = "") {
    if (isSyncing) {
      return;
    }
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => flushSync(reason), 400);
  }

  // ===== Create checklist =====
  async function createChecklist() {
    if (creating || isSyncing) return;
    const title = (newTitle || getTranslation("checklist")).trim();
    const description = (newDesc || "").trim();

    try {
      setCreating(true);

      const resp = await Services.CreateChecklist(
        token,
        title,
        !!newStep,
        selectedProjectID,
        description
      );
      const newLists = await loadLists();
      const latest =
        newLists
          .filter(
            (x) =>
              x.title === title &&
              (x.description === description ||
                (description === "" &&
                  (x.description === "No Description" ||
                    x.description === "")))
          )
          .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))[0] || null;

      if (latest) {
        setActiveListId(latest.id);
      }
      setIsCreateOpen(false);
    } catch (e) {
      console.error("[Checklist] CreateChecklist -> error:", e);
    } finally {
      setCreating(false);
    }
  }

  // ===== Update checklist (meta: title/description + mevcut items) =====
  function openEditModal() {
    if (!activeList || isSyncing) return;
    setEditTitle(activeList.title || "");
    setEditDesc(activeList.description || "");
    setEditStep(!!activeList.step); // görsel bilgi
    setIsEditOpen(true);
  }

  async function updateChecklist() {
    if (!activeList || updating) return;

    // Zaten bir UpdateChecklist uçuyorsa bunu başlatma
    if (syncInFlightRef.current) {
      console.warn(
        "[Checklist] UpdateChecklist (meta) skipped because UpdateChecklist is in flight"
      );
      return;
    }

    const title = (editTitle || getTranslation("checklist")).trim();
    const description = (editDesc || "").trim();
    const elements = buildElementsPayload(itemsRef.current);
    const isQueueRestricted = !!editStep;

    try {
      syncInFlightRef.current = true;
      setIsSyncing(true);
      setUpdating(true);

      const resp = await Services.UpdateChecklist(
        token,
        title,
        elements,
        description,
        activeList.id,
        isQueueRestricted
      );

      await loadLists();

      setIsEditOpen(false);
    } catch (e) {
      console.error("[Checklist] UpdateChecklist (meta) -> error:", e);
    } finally {
      setUpdating(false);
      syncInFlightRef.current = false;
      setIsSyncing(false);
    }
  }

  // ===== Delete checklist =====
  function openDeleteConfirm() {
    if (!activeList || isSyncing) return;
    setIsDeleteOpen(true);
  }

  async function deleteChecklistConfirmed() {
    if (!activeList || deleting || isSyncing) return;
    try {
      setDeleting(true);

      const resp = await Services.DeleteChecklist(token, activeList.id);
      await loadLists();

      setActiveListId("");
      setIsDeleteOpen(false);
    } catch (e) {
      console.error("[Checklist] DeleteChecklist -> error:", e);
    } finally {
      setDeleting(false);
    }
  }

  // ===== Item actions (flat liste üzerinde) =====
  const canSubmitNewItem = text.trim().length > 0;

  function addItem() {
    const value = text.trim();
    if (!activeListId) return;
    if (!canSubmitNewItem) return;
    if (isSyncing) return;

    setItems((prev) => {
      const next = [
        ...prev,
        {
          id: "tmp-" + crypto.randomUUID(),
          text: value,
          done: false,
          priority,
          assignee: assigneeId,
          due: due instanceof Date && !isNaN(due) ? new Date(due) : null,
          createdAt: Date.now(),
          parentId: null,
        },
      ].map((it, idx) => ({ ...it, order: idx }));
      return next;
    });

    setText("");
    setDue(null);
    setDueResetKey((k) => k + 1);
    setPriority("orta");
    setAssigneeId("");
    inputRef.current?.focus();

    scheduleSync("add-item");
  }

  function addSubItem(parentId) {
    if (!activeListId || isSyncing) return;

    setItems((prev) => {
      const next = [
        ...prev,
        {
          id: "tmp-" + crypto.randomUUID(),
          text: getTranslation("addSubItem"),
          done: false,
          priority: "orta",
          assignee: "",
          due: null,
          createdAt: Date.now(),
          parentId,
        },
      ].map((it, idx) => ({ ...it, order: idx }));
      return next;
    });

    scheduleSync("add-sub-item");
  }

  function handleNewItemKeyDown(e) {
    if (e.key === "Enter") {
      if (!canSubmitNewItem || isSyncing) {
        e.preventDefault();
        return;
      }
      addItem();
    }
  }

  function toggle(id) {
    if (isSyncing) return;

    const list = itemsRef.current;
    const idx = list.findIndex((i) => i.id === id);
    if (idx === -1) return;

    const item = list[idx];

    // Sıralı adım (step) kısıtı devam ediyor
    if (activeStep) {
      if (!item.done && isToggleDisabled(idx, item)) {
        return;
      }
    }

    setItems((prev) => {
      // id -> item map / parentId -> childrenIds map
      const byParent = new Map();
      prev.forEach((it) => {
        const key = it.parentId || null;
        if (!byParent.has(key)) byParent.set(key, []);
        byParent.get(key).push(it.id);
      });

      // Kopya array üzerinde çalışalım
      const next = prev.map((it) => ({ ...it }));

      const findIndexById = (arr, targetId) =>
        arr.findIndex((x) => x.id === targetId);

      const targetIndex = findIndexById(next, id);
      if (targetIndex === -1) return prev;

      const nextDone = !next[targetIndex].done;

      // Parent’ın tüm altlarını (sonsuz derinliğe kadar) güncelle
      const setDescendantsDone = (parentId) => {
        const childrenIds = byParent.get(parentId) || [];
        childrenIds.forEach((cid) => {
          const cIdx = findIndexById(next, cid);
          if (cIdx === -1) return;
          next[cIdx].done = nextDone;
          // Çocuğun çocuklarını da güncelle
          setDescendantsDone(cid);
        });
      };

      // Önce hedef item’i, sonra tüm çocuklarını güncelle
      next[targetIndex].done = nextDone;
      setDescendantsDone(id);

      // Üst parent’ların state’ini yine çocuklara göre otomatik hesapla
      return recomputeParentDoneState(next);
    });

    scheduleSync("toggle");
  }

  function remove(id) {
    if (isSyncing) return;

    setItems((prev) => {
      const toDelete = new Set();
      const flat = [...prev];

      const collect = (targetId) => {
        toDelete.add(targetId);
        flat
          .filter((i) => i.parentId === targetId)
          .forEach((child) => collect(child.id));
      };

      collect(id);

      const next = flat
        .filter((i) => !toDelete.has(i.id))
        .map((it, idx) => ({ ...it, order: idx }));

      return recomputeParentDoneState(next);
    });
    scheduleSync("remove");
  }

  function updateText(id, nextText) {
    if (isSyncing) return;
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, text: nextText } : i))
    );
    scheduleSync("update-text");
  }

  function updatePriorityLocal(id, next) {
    if (isSyncing) return;
    setItems((prev) =>
      prev.map((i) => (i.id === id ? { ...i, priority: next } : i))
    );
    scheduleSync("update-priority");
  }

  function updateAssigneeLocal(id, nextUserID) {
    if (isSyncing) return;
    setItems((prev) =>
      prev.map((i) =>
        i.id === id ? { ...i, assignee: nextUserID } : i
      )
    );
    scheduleSync("update-assignee");
  }

  function updateDueLocal(id, nextDate) {
    if (isSyncing) return;
    setItems((prev) =>
      prev.map((i) =>
        i.id === id
          ? { ...i, due: nextDate instanceof Date ? nextDate : null }
          : i
      )
    );
    scheduleSync("update-due");
  }

  function move(id, dir) {
    if (isSyncing) return;
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.id === id);
      if (idx === -1) return prev;
      const arr = [...prev];
      const swapWith = dir === "up" ? idx - 1 : idx + 1;
      if (swapWith < 0 || swapWith >= arr.length) return prev;
      [arr[idx], arr[swapWith]] = [arr[swapWith], arr[idx]];
      return arr.map((it, i) => ({ ...it, order: i }));
    });
    scheduleSync("reorder-move");
  }

  // ===== DnD: aynı parentId içindeki kardeşler arasında sırala =====
  function onDragStart(id) {
    if (isSyncing) return;
    setDraggingId(id);
  }

  function onDragOver(e) {
    if (isSyncing) return;
    e.preventDefault();
  }

  function onDropOver(targetId) {
    if (isSyncing) return;

    setItems((prev) => {
      if (!draggingId || draggingId === targetId) return prev;

      const fromItem = prev.find((i) => i.id === draggingId);
      const targetItem = prev.find((i) => i.id === targetId);
      if (!fromItem || !targetItem) return prev;

      const parentKeyFrom = fromItem.parentId || null;
      const parentKeyTarget = targetItem.parentId || null;

      if (parentKeyFrom !== parentKeyTarget) {
        return prev;
      }

      const siblings = prev.filter(
        (i) => (i.parentId || null) === parentKeyFrom
      );

      const siblingsSorted = siblings.slice().sort((a, b) => {
        const ao = typeof a.order === "number" ? a.order : 999999;
        const bo = typeof b.order === "number" ? b.order : 999999;
        return ao - bo;
      });

      const fromIndex = siblingsSorted.findIndex((i) => i.id === draggingId);
      const toIndex = siblingsSorted.findIndex((i) => i.id === targetId);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) {
        return prev;
      }

      const reordered = siblingsSorted.slice();
      const [moved] = reordered.splice(fromIndex, 1);
      reordered.splice(toIndex, 0, moved);

      const orderUpdates = new Map();
      reordered.forEach((item, idx) => {
        orderUpdates.set(item.id, idx);
      });

      const next = prev.map((it) => {
        if (orderUpdates.has(it.id)) {
          return { ...it, order: orderUpdates.get(it.id) };
        }
        return it;
      });

      return next;
    });

    setDraggingId(null);
    scheduleSync("reorder-dnd");
  }

  function completeAll() {
    if (isSyncing) return;

    setItems((prev) => {
      const allDone = prev.map((i) => ({ ...i, done: true }));
      return recomputeParentDoneState(allDone);
    });
    scheduleSync("complete-all");
  }

  function clearCompleted() {
    if (isSyncing) return;
    setItems((prev) =>
      prev
        .filter((i) => !i.done)
        .map((it, idx) => ({ ...it, order: idx }))
    );
    scheduleSync("clear-completed");
  }

  function triggerEdit(id) {
    if (isSyncing) return;
    setEditSignalMap((m) => ({
      ...m,
      [id]: (m[id] || 0) + 1,
    }));
  }

  // ===== UI =====
  return (
    <div className="checklist-container">
      <ModuleSidebar
        title={getTranslation("checklist")}
        menus={menus}
        selectedSubmenu={activeListId}
        onSubmenuClick={handleSidebarClick}
        addEntitiy={handleAddEntity}
        showSearch={false}
      />

      <div className="checklist-content" style={{ position: "relative" }}>
        {/* Global overlay: tıklamaları bloklar ama stil değiştirmez */}
        {isSyncing && (
          <div
            className="checklist-sync-overlay"
            style={{
              position: "absolute",
              inset: 0,
              zIndex: 50,
              background: "transparent",
            }}
          />
        )}

        <div className="checklist-inner-content">
          {/* CREATE */}
          {isCreateOpen && (
            <CreateOrEditChecklistModal
              mode="create"
              processing={creating}
              title={newTitle}
              setTitle={setNewTitle}
              desc={newDesc}
              setDesc={setNewDesc}
              step={newStep}
              setStep={setNewStep}
              onCancel={() => setIsCreateOpen(false)}
              onSubmit={createChecklist}
              titleInputRef={modalTitleInputRef}
              allowStepEdit={!isSyncing}
            />
          )}

          {/* EDIT */}
          {isEditOpen && (
            <CreateOrEditChecklistModal
              mode="edit"
              processing={updating}
              title={editTitle}
              setTitle={setEditTitle}
              desc={editDesc}
              setDesc={setEditDesc}
              step={editStep}
              setStep={setEditStep}
              onCancel={() => setIsEditOpen(false)}
              onSubmit={updateChecklist}
              titleInputRef={editTitleRef}
              allowStepEdit={!isSyncing}
            />
          )}

          {/* DELETE */}
          {isDeleteOpen && activeList && (
            <AlertBox
              isOpen={isDeleteOpen}
              type="error"
              title={getTranslation("deleteConfirmation")}
              message={getTranslation("confirmDeleteChecklist", {
                checklistTitle: activeList.title,
              })}
              onCancel={() => !deleting && setIsDeleteOpen(false)}
              onConfirm={deleteChecklistConfirmed}
              cancelText={getTranslation("cancel")}
              confirmText={
                deleting
                  ? getTranslation("deleting")
                  : getTranslation("delete")
              }
            />
          )}

          <header className="checklist-header">
            <div className="checklist-headings">
              <h1 className="checklist-title">
                {activeList ? activeList.title : getTranslation("checklist")}
              </h1>
              <p className="checklist-muted">
                {activeList
                  ? activeList.description ||
                  (activeList.step
                    ? getTranslation("stepRequired")
                    : getTranslation("simpleFastOrganizedTodo"))
                  : getTranslation("simpleFastOrganizedTodo")}
              </p>
            </div>
            <div className="checklist-progress-box">
              <div
                className="progress-wrap"
                aria-label={getTranslation("completionRate")}
              >
                <div
                  className="cl-progress-bar"
                  style={{ width: `${stats.pct}%` }}
                />
              </div>
              <p className="checklist-caption">
                {getTranslation("completionRate")}: {stats.done}/{stats.total}
              </p>
            </div>
          </header>

          {!activeList && (
            <div className="empty" style={{ marginTop: 12 }}>
              {getTranslation("pleaseSelectChecklist")}
            </div>
          )}

          {activeList && (
            <>
              <div
                className="checklist-toolbar"
                style={{ marginBottom: 8 }}
              >
                <div className="toolbar-actions">
                  <button
                    className="btn btn-ghost"
                    onClick={openEditModal}
                  >
                    {getTranslation("editChecklist")}
                  </button>
                  <button
                    className="btn btn-ghost"
                    onClick={openDeleteConfirm}
                  >
                    {getTranslation("delete")}
                  </button>
                </div>
              </div>

              <div
                className="checklist-row"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  ref={inputRef}
                  placeholder={getTranslation("addNewItem")}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  onKeyDown={handleNewItemKeyDown}
                  className="inp"
                />

                <Dropdown
                  options={priorityOptions}
                  placeholder={getTranslation("priority")}
                  value={priority}
                  style={{ width: "125px" }}
                  onChange={(e) => setPriority(e.target.value)}
                />

                <Dropdown
                  options={assigneeOptions.filter(
                    (o) => o.value !== ""
                  )}
                  placeholder={getTranslation("assignee")}
                  value={assigneeId}
                  onChange={(e) => setAssigneeId(e.target.value)}
                />

                <div onClick={(e) => e.stopPropagation()}>
                  <DatePicker
                    key={dueResetKey}
                    toggleLabel={getTranslation("selectDate")}
                    initialDate={due}
                    onDateSelect={(d) => !isSyncing && setDue(d)}
                    dropdownPosition="bottom"
                    dropdownAlign="right"
                  />
                </div>

                <button
                  onClick={addItem}
                  className="btn btn-primary"
                  disabled={!canSubmitNewItem}
                  title={
                    !canSubmitNewItem
                      ? getTranslation("descriptionRequired")
                      : ""
                  }
                >
                  {getTranslation("add")}
                </button>
              </div>

              <div
                className="checklist-toolbar"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="segmented">
                  <SegmentButton
                    onClick={() => !isSyncing && setFilter("all")}
                    active={filter === "all"}
                  >
                    {getTranslation("all")}
                  </SegmentButton>
                  <SegmentButton
                    onClick={() => !isSyncing && setFilter("active")}
                    active={filter === "active"}
                  >
                    {getTranslation("active")}
                  </SegmentButton>
                  <SegmentButton
                    onClick={() => !isSyncing && setFilter("done")}
                    active={filter === "done"}
                  >
                    {getTranslation("completed")}
                  </SegmentButton>
                </div>

                <div className="toolbar-actions">
                  <button
                    onClick={completeAll}
                    className="btn btn-ghost"
                  >
                    {getTranslation("completeAll")}
                  </button>
                  <button
                    onClick={clearCompleted}
                    className="btn btn-ghost"
                  >
                    {getTranslation("clearCompleted")}
                  </button>
                </div>
              </div>

              <ul className="list">
                {treeItems.length === 0 && (
                  <li className="empty">
                    {getTranslation("noItemsForFilter")}
                  </li>
                )}
                {treeItems.map((item) => (
                  <ChecklistItemRow
                    key={item.id}
                    item={item}
                    level={0}
                    filter={filter}
                    isItemVisible={isItemVisible}
                    getIndexById={getIndexById}
                    isToggleDisabled={isToggleDisabled}
                    findUserLabelById={findUserLabelById}
                    assigneeOptions={assigneeOptions}
                    editSignalMap={editSignalMap}
                    toggle={toggle}
                    updateText={updateText}
                    updatePriorityLocal={updatePriorityLocal}
                    updateAssigneeLocal={updateAssigneeLocal}
                    updateDueLocal={updateDueLocal}
                    move={move}
                    remove={remove}
                    triggerEdit={triggerEdit}
                    addSubItem={addSubItem}
                    draggingId={draggingId}
                    onDragStart={onDragStart}
                    onDragOver={onDragOver}
                    onDropOver={onDropOver}
                    isSyncing={isSyncing}
                  />
                ))}
              </ul>

              <footer className="checklist-footer">
                <span className="checklist-caption">
                  {getTranslation("activeLabel")} {stats.active}
                </span>
                <span className="dot" />
                <span className="checklist-caption">
                  {getTranslation("total")}: {stats.total}
                </span>
              </footer>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ===== Recursive Row Component (nested items) ===== */
function ChecklistItemRow({
  item,
  level,
  filter,
  isItemVisible,
  getIndexById,
  isToggleDisabled,
  findUserLabelById,
  assigneeOptions,
  editSignalMap,
  toggle,
  updateText,
  updatePriorityLocal,
  updateAssigneeLocal,
  updateDueLocal,
  move,
  remove,
  triggerEdit,
  addSubItem,
  draggingId,
  onDragStart,
  onDragOver,
  onDropOver,
  isSyncing,
}) {
  const { getTranslation } = useLanguage();
  const realIdx = getIndexById(item.id);
  const disabledStep = isToggleDisabled(realIdx, item);
  const assigneeLabel = findUserLabelById(item.assignee);
  const visible = isItemVisible(item);

  const [assigneeOpen, setAssigneeOpen] = useState(false);
  const assigneeRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (
        assigneeRef.current &&
        !assigneeRef.current.contains(e.target)
      ) {
        setAssigneeOpen(false);
      }
    }
    if (assigneeOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [assigneeOpen]);

  return (
    <>
      {visible && (
        <li
          className={`list-item ${draggingId === item.id ? "dragging" : ""}`}
          style={{ marginLeft: level * 20 }}
          draggable={!isSyncing}
          onDragStart={() => !isSyncing && onDragStart(item.id)}
          onDragOver={(e) => !isSyncing && onDragOver(e)}
          onDrop={() => !isSyncing && onDropOver(item.id)}
          onClick={() =>
            !disabledStep && !isSyncing && toggle(item.id)
          }
        >
          <label
            className="item-left"
            onClick={(e) => e.stopPropagation()}
          >
            <span className="drag-handle" aria-hidden>
              ⋮⋮
            </span>
            <input
              type="checkbox"
              checked={item.done}
              onChange={() =>
                !disabledStep && !isSyncing && toggle(item.id)
              }
              className="checkbox"
              disabled={disabledStep}
              title={
                disabledStep
                  ? getTranslation("completePreviousFirst")
                  : ""
              }
            />
            <EditableText
              value={item.text}
              onChange={(val) => updateText(item.id, val)}
              done={item.done}
              editSignal={editSignalMap[item.id]}
              disabled={isSyncing}
            />
          </label>

          <div
            className="item-right"
            onClick={(e) => e.stopPropagation()}
          >
            {/* PRIORITY CHIP */}
            <PriorityTag
              value={item.priority}
              onChange={(p) => !isSyncing && updatePriorityLocal(item.id, p)}
            />

            {/* ASSIGNEE CHIP */}
            <div
              className="assignee-wrap"
              ref={assigneeRef}
              style={{ position: "relative" }}
            >
              <button
                type="button"
                className="assignee-badge"
                title={getTranslation("assignee")}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isSyncing) return;
                  setAssigneeOpen((o) => !o);
                }}
              >
                {assigneeLabel || getTranslation("none")}
              </button>

              {assigneeOpen && !isSyncing && (
                <div
                  className="assignee-popover"
                  style={{
                    position: "absolute",
                    top: "110%",
                    right: 0,
                    zIndex: 20,
                    background: "var(--surface, #222)",
                    border: "1px solid var(--border, #444)",
                    borderRadius: 8,
                    padding: 6,
                    minWidth: 160,
                    maxHeight: 260,
                    overflowY: "auto",
                    boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {assigneeOptions.map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => {
                        updateAssigneeLocal(item.id, opt.value);
                        setAssigneeOpen(false);
                      }}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        border: "none",
                        background: "transparent",
                        padding: "6px 8px",
                        fontSize: 12,
                        cursor: "var(--cursor, pointer)",
                        color: "var(--text, #fff)",
                        opacity:
                          String(opt.value) === String(item.assignee)
                            ? 1
                            : 0.85,
                        fontWeight:
                          String(opt.value) === String(item.assignee)
                            ? 600
                            : 400,
                      }}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div>
              <DatePicker
                toggleLabel={
                  item.due instanceof Date && !isNaN(item.due)
                    ? item.due.toLocaleDateString()
                    : getTranslation("selectDate")
                }
                initialDate={item.due || null}
                onDateSelect={(d) => !isSyncing && updateDueLocal(item.id, d)}
                dropdownPosition="bottom"
                dropdownAlign="right"
              />
            </div>

            <div className="icon-actions">
              <IconButton
                label={getTranslation("addSubItem")}
                onClick={() => addSubItem(item.id)}
                disabled={isSyncing}
              >
                +
              </IconButton>
              <IconButton
                label={getTranslation("edit")}
                onClick={() => triggerEdit(item.id)}
                disabled={isSyncing}
              >
                ✎
              </IconButton>
              <IconButton
                label={getTranslation("moveUp")}
                onClick={() => move(item.id, "up")}
                disabled={isSyncing}
              >
                ↑
              </IconButton>
              <IconButton
                label={getTranslation("moveDown")}
                onClick={() => move(item.id, "down")}
                disabled={isSyncing}
              >
                ↓
              </IconButton>
              <IconButton
                label={getTranslation("delete")}
                onClick={() => remove(item.id)}
                danger
                disabled={isSyncing}
              >
                ✕
              </IconButton>
            </div>
          </div>
        </li>
      )}

      {item.children &&
        item.children.map((child) => (
          <ChecklistItemRow
            key={child.id}
            item={child}
            level={level + 1}
            filter={filter}
            isItemVisible={isItemVisible}
            getIndexById={getIndexById}
            isToggleDisabled={isToggleDisabled}
            findUserLabelById={findUserLabelById}
            assigneeOptions={assigneeOptions}
            editSignalMap={editSignalMap}
            toggle={toggle}
            updateText={updateText}
            updatePriorityLocal={updatePriorityLocal}
            updateAssigneeLocal={updateAssigneeLocal}
            updateDueLocal={updateDueLocal}
            move={move}
            remove={remove}
            triggerEdit={triggerEdit}
            addSubItem={addSubItem}
            draggingId={draggingId}
            onDragStart={onDragStart}
            onDragOver={onDragOver}
            onDropOver={onDropOver}
            isSyncing={isSyncing}
          />
        ))}
    </>
  );
}

/* ===== Create/Edit Modal ===== */
function CreateOrEditChecklistModal({
  mode, // "create" | "edit"
  processing,
  title,
  setTitle,
  desc,
  setDesc,
  step,
  setStep,
  onCancel,
  onSubmit,
  titleInputRef,
  allowStepEdit,
}) {
  const { getTranslation } = useLanguage();

  const heading =
    mode === "edit"
      ? getTranslation("editChecklist")
      : getTranslation("createNewChecklist");

  const submitText = processing
    ? mode === "edit"
      ? getTranslation("saving")
      : getTranslation("creating")
    : mode === "edit"
      ? getTranslation("save")
      : getTranslation("create");

  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        zIndex: 9999,
        display: "grid",
        placeItems: "center",
      }}
      onClick={onCancel}
    >
      <div
        style={{
          minWidth: 520,
          maxWidth: "90vw",
          background: "var(--modal-sidebar-bg)",
          color: "var(--projects-menu-font-color)",
          border: "1px solid var(--modal-sidebar-border)",
          borderRadius: 12,
          boxShadow: "0 10px 30px rgba(0,0,0,.25)",
          padding: 16,
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <h3
          style={{
            margin: "0 0 10px",
            fontSize: 16,
            fontWeight: 800,
            color: "var(--invite-collabrators-title-color)",
          }}
        >
          {heading}
        </h3>

        <div
          style={{ display: "flex", flexDirection: "column", gap: 10 }}
        >
          <input
            ref={titleInputRef}
            className="inp"
            placeholder={getTranslation("checklistTitle")}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <textarea
            className="inp"
            placeholder={getTranslation("description")}
            value={desc}
            onChange={(e) => setDesc(e.target.value)}
            rows={3}
            style={{ resize: "vertical" }}
          />
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "8px 10px",
              borderRadius: 10,
              border: "1px solid var(--input-field-border)",
              background: "var(--input-field-bg)",
              color: "var(--input-field-color)",
              cursor: allowStepEdit
                ? "var(--cursor, pointer)"
                : "not-allowed",
              opacity: allowStepEdit ? 1 : 0.7,
            }}
            title={
              allowStepEdit
                ? getTranslation("stepsMustBeSequential")
                : getTranslation("stepInfoNotEditable")
            }
          >
            <input
              type="checkbox"
              checked={step}
              onChange={(e) =>
                allowStepEdit && setStep(e.target.checked)
              }
              disabled={!allowStepEdit}
              style={{
                cursor: allowStepEdit
                  ? "var(--cursor, pointer)"
                  : "not-allowed",
              }}
            />
            {getTranslation("stepsSequential")}
          </label>
        </div>

        <div
          style={{
            display: "flex",
            gap: 8,
            marginTop: 12,
            justifyContent: "flex-end",
          }}
        >
          <button
            className="btn btn-ghost"
            onClick={onCancel}
            disabled={processing}
          >
            {getTranslation("cancel")}
          </button>
          <button
            className="btn btn-primary"
            onClick={onSubmit}
            disabled={processing}
          >
            {submitText}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditableText({ value, onChange, done, editSignal, disabled }) {
  const { getTranslation } = useLanguage();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing) {
      inputRef.current?.focus();
    }
  }, [editing]);

  useEffect(() => {
    setDraft(value);
  }, [value]);

  // Sadece editSignal değiştiğinde (icon'dan "edit" basınca) inputu aç
  useEffect(() => {
    if (editSignal !== undefined) {
      setEditing(true);
    }
  }, [editSignal]);

  function commit() {
    const v = draft.trim();
    onChange(v || value);
    setEditing(false);
  }

  // Artık editing açıkken disabled'a bakmıyoruz; input kapanmayacak
  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => e.key === "Enter" && commit()}
        className="edit-input"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <span
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (disabled) return; // sync sırasında yeni edit açma
        setEditing(true);
      }}
      title={getTranslation("doubleClickToEdit")}
      className={`item-text ${done ? "done" : ""}`}
      onClick={(e) => e.stopPropagation()}
    >
      {value}
    </span>
  );
}

function PriorityTag({ value, onChange }) {
  const { getTranslation } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  const priorityOptions = [
    { value: "düşük", label: getTranslation("low") },
    { value: "orta", label: getTranslation("medium") },
    { value: "yüksek", label: getTranslation("high") },
  ];

  const valueToKey = {
    düşük: "low",
    orta: "medium",
    yüksek: "high",
  };

  const badgeLabel = getTranslation(valueToKey[value] || "medium");

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    if (open) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [open]);

  return (
    <div
      className="priority-wrap"
      ref={wrapRef}
      style={{ position: "relative" }}
    >
      <button
        type="button"
        className={`priority-badge prio-${value}`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((o) => !o);
        }}
        title={getTranslation("priority")}
      >
        {badgeLabel.toUpperCase()}
      </button>

      {open && (
        <div
          className="priority-popover"
          style={{
            position: "absolute",
            top: "110%",
            left: 0,
            zIndex: 20,
            background: "var(--surface, #222)",
            border: "1px solid var(--border, #444)",
            borderRadius: 8,
            padding: 6,
            minWidth: 140,
            boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {priorityOptions.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => {
                onChange(opt.value);
                setOpen(false);
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                border: "none",
                background: "transparent",
                padding: "6px 8px",
                fontSize: 12,
                cursor: "var(--cursor, pointer)",
                color: "var(--text, #fff)",
                opacity: opt.value === value ? 1 : 0.85,
                fontWeight: opt.value === value ? 600 : 400,
              }}
            >
              {opt.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SegmentButton({ active, children, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`segment-btn ${active ? "active" : ""}`}
    >
      {children}
    </button>
  );
}

function IconButton({ children, onClick, label, danger, disabled }) {
  return (
    <button
      aria-label={label}
      onClick={(e) => {
        e.stopPropagation();
        if (disabled) return;
        onClick?.();
      }}
      title={label}
      className={`cl-icon-btn ${danger ? "danger" : ""}`}
    >
      {children}
    </button>
  );
}